import {
	Directive,
	Injector,
	ViewChild,
} from '@angular/core';
import {
	ActivatedRoute,
	Router,
} from '@angular/router';
import {
	GridDataResult,
	PageChangeEvent,
	PagerSettings,
	SelectableMode,
} from '@progress/kendo-angular-grid';
import {
	CompositeFilterDescriptor,
	SortDescriptor,
} from '@progress/kendo-data-query';
import {
	copyIcon,
	fileAddIcon,
	fileCsvIcon,
	infoCircleIcon,
	trashIcon,
} from '@progress/kendo-svg-icons';
import { instanceToInstance } from 'class-transformer';
import { ToastrService } from 'ngx-toastr';

import { AdvanceSearchGridComponent, GridStateViewService } from '@aex/shared/ui-components';

import { DialogManagerService, ExportService } from '../services';

import {
	IPagedData,
	CanComponentDeactivate,
	IYesNo,
	ActionItem,
	GridColumnItem,
	GridState,
	IAdvanceGridSearch,
	isGridStateIdentical,
	LoadGridStateRequest,
	PAGE_SIZES,
} from '..';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { isNonNull, WithDestroy } from '@aex/ngx-toolbox';

@Directive()
export abstract class BaseGridComponent<TSearchDataItem, TFilterItem> extends WithDestroy() implements CanComponentDeactivate {

	@ViewChild(AdvanceSearchGridComponent) advanceSearchGridComponent: AdvanceSearchGridComponent;

	public readonly svgCsvIcon = fileCsvIcon;
	public readonly svgCopyIcon = copyIcon;
	public readonly svgTrashIcon = trashIcon;
	public readonly svgFileAddIcon = fileAddIcon;
	public readonly svgInfoCircleIcon = infoCircleIcon;

	protected readonly toastr: ToastrService = this.injector.get(ToastrService);
	protected readonly dialogManagerService: DialogManagerService = this.injector.get(DialogManagerService);
	protected readonly gridStateViewService: GridStateViewService = this.injector.get(GridStateViewService);
	protected readonly router: Router = this.injector.get(Router);
	protected readonly route: ActivatedRoute = this.injector.get(ActivatedRoute);
	protected readonly exportService: ExportService = this.injector.get(ExportService);

	protected constructor(
		protected readonly injector: Injector,
	) {
		super(injector);
	}


	public canDeactivate(): Observable<boolean> | boolean {
		if (!isNonNull(this.loadedGridState))
			return true;

		// Get Current grid state
		const currentGridState = this.advanceSearchGridComponent.currentGridState;
		currentGridState.advanceFilterActive = this.advanceFilterActive;
		currentGridState.hasAdvancedSearch = true;
		currentGridState.advGridSearch = this.advanceSearchFilterItem;

		// Compare with loaded Grid State
		const isGridStateSame = isGridStateIdentical(this.loadedGridState, currentGridState);
		if (isGridStateSame)
			return true;

		const canDeactivateMessage = `Grid State has changed. Do you want to save the changes?`;
		return this.dialogManagerService.openYesNoDialog(
			'Grid State Changes',
			canDeactivateMessage,
		).pipe(
			map
			((confirm: boolean) => {
					if (confirm)
						this.saveGridStateEvent(currentGridState);

					return true;
				},
			),
		);
	}

	public get gridData(): TSearchDataItem[] {
		return this.pagedData.items;
	}

	public pagedData: IPagedData<TSearchDataItem> = {
		page: 1,
		count: 0,
		total: 0,
		items: [],
	};

	private _filteredGridData: TSearchDataItem[] = [];
	public get filteredGridData(): TSearchDataItem[] {
		return this._filteredGridData;
	};

	public totalRecordCount: number = 0;

	public gridDataResult: GridDataResult = {
		data: this.filteredGridData,
		total: this.totalRecordCount,
	};

	public set filteredGridData(value: TSearchDataItem[]) {
		this._filteredGridData = value;
		const gridDataResult = {
			data: this.filteredGridData,
			total: this.totalRecordCount,
		};
		this.gridDataResult = { ...gridDataResult };
	}

	public columns: GridColumnItem[] = [];
	public selectedColumns: string[] = [];

	// Pagination
	public pageSize = 20;
	public buttonCount = 5;
	public sizes = PAGE_SIZES;
	public skip = 0;
	public currentPage = 1;
	public pageSettings: PagerSettings = {
		buttonCount: this.buttonCount,
		info: true,
		type: 'numeric',
		pageSizes: this.sizes,
		previousNext: true,
	};

	// Advance Filter
	public advanceFilterActive: boolean = false;
	public advanceSearchFilterItem: TFilterItem | null = null;

	public toggleAdvanceSearch: boolean = false;

	// Grid Filtering
	public sort: SortDescriptor[] = [{ field: 'name', dir: 'asc' }]; 		// Grid Sort Value
	protected readonly defaultFilter: CompositeFilterDescriptor = { logic: 'and', filters: [] };

	public kendoGridSelectKeyId: string = this.getKendoGridSelectKeyId(); // Key Id for the Kendo Grid Select
	public showCheckboxColumn: boolean = this.getShowCheckboxColumn();   	// Show the checkbox column
	public gridSelectable: SelectableMode = this.checkboxSelectable();   	// 'single' | 'multiple'
	public selectedItems: string[] = [];                       // Selected row items

	public hideQuickToolsList: IYesNo[] = [
		{ id: true, name: 'Show Quick Tools' },
		{ id: false, name: 'Hide Quick Tools' },
	];

	public quickButtonDisabled: boolean = true;
	public hideQuickTools: boolean = true;

	protected readonly exportFileName = this.getExportFileName();

	public gridPageChangeEvent(event: PageChangeEvent): void {
		this.pageSize = event.take;
		const currentPage = Math.floor(event.skip / this.pageSize) + 1; // Calculate the page number
		this.skip = event.skip;
		this.currentPage = currentPage;
		this.fetchGridData(this.currentPage, this.pageSize, this.skip);
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public setAdvanceSearchFilterItemEvent(advancedSearchFilterItem: any): void {
		this.advanceSearchFilterItem = advancedSearchFilterItem;
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public nameHyperlinkActionEvent(event: any): void {
		var actionItem = event as ActionItem<TSearchDataItem>;
		this.nameHyperlinkAction(actionItem.fieldName, actionItem.dataItem);
	}

	// Grid State
	public readonly gridViewId: string = this.getGridViewName();
	public gridState: GridState = new GridState();                   // Grid State

	public loadedGridStateRequest: LoadGridStateRequest | null;

	public get loadedGridState(): GridState | null {
		return this.loadedGridStateRequest?.gridState;
	}

	public saveGridStateEvent(gridState: GridState): void {
		gridState.advGridSearch = instanceToInstance(this.advanceSearchFilterItem);
		this.gridState = gridState;
		this.gridStateViewService.saveGridViewState(
			this.gridViewId,
			gridState,
		);
	}

	//#tdoo: portal2 - check if this routine is necessary
	protected restoreLoadedGridState(): void {
		if (!this.loadedGridState)
			return;

		// this.restoreLoadedGridState();
		// this.visibleColumns = restoreGridVisibleColumnOrder(this.columns, this.loadedGridState.visualColumnOrder);
	}

	public filterAdvSearchClick(): void {
		this.advanceFilterActive = true;
		this.currentPage = 1;
		this.toggleAdvanceSearch = false;
		this.fetchGridData(this.currentPage, this.pageSize, this.skip);
	}

	protected clearFilterAdvSearchClick(): void {
		this.clearAdvanceSearchFilter();
	}

	public cancelAdvSearchClick(): void {
		this.clearFilterAdvSearchClick();
		this.toggleAdvanceSearch = false;
		this.advanceFilterActive = false;
		this.selectedItems = [];
		this.currentPage = 1;
		this.fetchGridData(this.currentPage, this.pageSize, this.skip);
	}

	public checkBoxSelectionEvent(selectedItems: string[]): void {
		this.selectedItems = selectedItems;
		this.quickButtonDisabled = this.selectedItems.length === 0;
		this.onCheckBoxSelectionChange();
	}

	public loadGridStateEvent(loadGridStateRequest: LoadGridStateRequest): void {
		if (loadGridStateRequest.clearData)
			this.loadedGridStateRequest = loadGridStateRequest;

		const gridState = loadGridStateRequest.gridState;
		if (gridState?.hasAdvancedSearch) {
			this.advanceFilterActive = gridState.advanceFilterActive;
			this.setAdvanceSearchFilterItem(gridState.advGridSearch);
			loadGridStateRequest.gridState = instanceToInstance(gridState);
		}

		this.loadedGridStateRequest = loadGridStateRequest;

		// Restore the grid data and update the visible columns?
		if (!loadGridStateRequest.reloadData)
			return;

		this.fetchGridData(gridState.currentPage, gridState.pageSize, this.skip);
	}

	// Abstract Methods
	protected abstract getGridViewName(): string;

	protected getShowCheckboxColumn(): boolean {
		return false;
	}

	protected checkboxSelectable(): SelectableMode {
		return 'multiple';
	}

	protected getExportFileName(): string {
		return `${this.getGridViewName()}.xlsx`;
	}

	protected abstract fetchGridData(pageNumber: number, pageSize: number, skip: number): void;

	protected abstract getKendoGridSelectKeyId(): string;

	public clearAdvanceSearchFilter(): void {
		/* do nothing by default*/
	}

	protected setAdvanceSearchFilterItem(_value: IAdvanceGridSearch): void {
		/* do nothing by default*/
	}

	protected nameHyperlinkAction(_field: string, _action: TSearchDataItem): void {/* do nothing by default*/
	}

	protected onCheckBoxSelectionChange(): void {/* do nothing by default*/
	}

}
